import { useTranslations } from "next-intl";
import { LoadingIndicator } from "@/app/(shop)/services/[slug]/components/loadingIndicator";
import { required } from "@/utils/inputValidators";
import { inputFieldTheme } from "@/utils/themes";
import React from "react";
import { ProfileIcon } from "../_icons/profileIcon";
import LocalizedPhoneInput from "../_localized/localizedPhoneInput";
import { ConfirmButton } from "../button/confirmButton";
import { TextButton } from "../button/textButton";
import { StyledInputField } from "../styledInputField/styledInputField";
import { isEmail } from "@/utils/inputValidators";
import { LoginFormFields } from "./loginInfoModal";
import { Merchant } from "@/dto/merchant";
import { UseFormReturn, UseFormSetValue } from "react-hook-form";
import CloseIcon from "@/components/_icons/closeIcon";
import useMediaQuery from "@/utils/useMediaQuery";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";

const perksEnabledLocales = ["en-US"];

export const LoginModalUsernameInput = ({
  merchant,
  form,
  setValue,
  phoneField,
  emailField,
  setErrorMessage,
  onRequestClose,
  merchantColor,
  showPhoneNumberInput,
  showEmailSwitching,
  setShowPhoneNumberInput,
  errorMessage,
  isLoading,
  isValid,
  // eslint-disable-next-line no-unused-vars
  captchaRef,
  showGuestCheckout,
  handleGuestCheckout,
}: {
  merchant: Merchant;
  form: UseFormReturn<LoginFormFields>;
  setValue: UseFormSetValue<LoginFormFields>;
  phoneField: keyof Pick<LoginFormFields, "phone">;
  emailField: keyof Pick<LoginFormFields, "email">;
  // eslint-disable-next-line no-unused-vars
  setErrorMessage: (message: string | null) => void;
  onRequestClose: () => void;
  merchantColor: string;
  showPhoneNumberInput: boolean;
  showEmailSwitching: boolean;
  // eslint-disable-next-line no-unused-vars
  setShowPhoneNumberInput: (show: boolean) => void;
  errorMessage: string | null;
  isLoading: boolean;
  isValid: boolean;
  // eslint-disable-next-line no-unused-vars
  captchaRef: any;
  showGuestCheckout: boolean;
  handleGuestCheckout: () => void;
}) => {
  const t = useTranslations("LoginModalUsernameInput");
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const perksEnabled = perksEnabledLocales.includes(merchant?.locale);
  return (
    <div className="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-[auto] sm:w-[70%] mx-auto overflow-hidden p-4 [@media(max-height:480px)]:h-[100%] [@media(max-height:480px)]:overflow-auto">
      <div className="flex justify-between items-center px-6 pt-6">
        <button
          onClick={() => {
            setValue(phoneField, "");
            setValue(emailField, "");
            form.clearErrors();
            setErrorMessage(null);
            onRequestClose();
          }}
          className="text-gray-600 hover:text-gray-900 ml-auto"
          data-testid={applyDataTestId(pom.modals.loginModalCloseButton)}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="px-4 pb-4 flex flex-col justify-center items-center max-h-[calc(100vh-6rem)]">
        <span className="shrink-0">
          <ProfileIcon brandingColor={merchantColor} />
        </span>
        <h1 className="mt-4 text-3xl font-medium text-center text-neutral-700 shrink">
          {t("title")}
        </h1>
        <p className="mt-4 text-base leading-6 text-center text-[15px] text-neutral-700 sm:w-[460px] shrink">
          {perksEnabled ? t("perksMessage") : t("noPerksMessage")}
        </p>
        <div className="my-5 w-full sm:w-1/2 flex flex-col items-center shrink">
          {/* TODO: ADD NEGATIVE FLOWS TO THESE INPUTS */}
          {showPhoneNumberInput && (
            <LocalizedPhoneInput
              name="phone"
              form={form}
              locale={merchant.locale}
              merchantColor={merchantColor}
            />
          )}
          {!showPhoneNumberInput && (
            <StyledInputField
              label={t("email")}
              name="email"
              form={form}
              validator={{
                isEmail: isEmail(),
                required: required(t("email")),
              }}
              theme={inputFieldTheme}
            />
          )}
        </div>
        {showEmailSwitching && (
          <div>
            <p className="font-normal text-[15px] text-center">
              {t("signUpOrLoginWithYour")}{" "}
              <span
                className="font-semibold cursor-pointer"
                style={{ color: merchantColor }}
                data-testid={applyDataTestId(pom.modals.loginModalSwitchInput)}
                onClick={() => {
                  setErrorMessage(null);
                  setShowPhoneNumberInput(!showPhoneNumberInput);
                }}
              >
                {showPhoneNumberInput
                  ? t("emailOption")
                  : t("phoneNumberOption")}
              </span>
              &nbsp;
              {t("instead")}
            </p>
          </div>
        )}
        {errorMessage && (
          <div className="text-red-600 text-sm mt-2 shrink">{errorMessage}</div>
        )}
        {isLoading ? (
          <div className="fixed bottom-0 sm:static">
            <LoadingIndicator />
          </div>
        ) : (
          <ConfirmButton
            disabled={!isValid}
            style={{
              backgroundColor: !isValid ? "#C7C7C7" : merchantColor,
            }}
            className="rounded-none fixed sm:static bottom-0 w-full px-16 py-5 sm:mt-11 max-w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap bg-[#228800] disabled:bg-[#C7C7C7] sm:rounded-lg sm:w-[250px] max-md:px-5 sm:max-md:mt-10 shrink"
            onClick={() => {
              if (errorMessage) {
                setErrorMessage(null);
              }
              captchaRef.current?.reset();
              captchaRef.current?.execute();
            }}
          >
            <p>{t("continue")}</p>
          </ConfirmButton>
        )}
        {showGuestCheckout && (
          <TextButton
            className={`fixed sm:static bottom-16 border-t border-[#e5e5e5] sm:border-none sm:mt-8 sm:mb-6 text-xl font-medium leading-7 text-center max-md:mt-10 ${
              isMobileScreen && "w-full h-16 rounded-none"
            }`}
            onClick={handleGuestCheckout}
            style={{ color: merchantColor }}
          >
            {t("guestCheckout")}
          </TextButton>
        )}
      </div>
    </div>
  );
};
